import type { FunctionComponent } from 'preact'
import { AIChatButton } from './AIChatButton'
import { AIChatWindow } from './AIChatWindow'
import { DelayedFeedbackBanner } from './DelayedFeedbackBanner'
import { useAIChat } from '@/hooks'
import { fieldUpdateHandler, aiChatSuppressed } from '@/state/aiChat'
import type { ContextBuilder } from '@/types/aiChat'
import type { QuizDraftDto } from '@/types/api-generated'
import { QUIZ_AI_FIELD, type QuizDraftDescription } from '@/utils/quizDraft'

interface AIChatWidgetProps {
  /** Optional custom context builder */
  contextBuilder?: ContextBuilder
  /** Whether to show notification indicator */
  hasNotification?: boolean
}

/**
 * Main AI Chat Widget component
 * Displays a floating button and chat window
 */
export const AIChatWidget: FunctionComponent<AIChatWidgetProps> = ({
  contextBuilder,
  hasNotification = false,
}) => {
  const {
    state,
    toggleChat,
    closeChat,
    minimizeChat,
    maximizeChat,
    sendUserMessage,
    applyToField,
    markQuizDraftApplied,
    executeAction,
    provideFeedback,
    provideMessageFeedback,
    hideDelayedFeedback,
  } = useAIChat()

  const handleToggleChat = () => {
    toggleChat(contextBuilder)
  }

  const resolveMessage = (messageId: string) => state.messages.find(m => m.id === messageId)

  const handleSelectVariation = async (messageId: string, variationIndex: number, value: string): Promise<boolean> => {
    const message = resolveMessage(messageId)
    const targetField = message?.targetFieldName
    if (!targetField || !fieldUpdateHandler.value) return false
    await applyToField(targetField, value, variationIndex, message?.backendIndex)
    return true
  }

  const handleSelectAllVariations = async (messageId: string, variations: string[]): Promise<boolean> => {
    const message = resolveMessage(messageId)
    const targetField = message?.targetFieldName
    if (!targetField || !fieldUpdateHandler.value) return false
    await applyToField(targetField, JSON.stringify(variations), undefined, message?.backendIndex)
    return true
  }

  // The call Use This makes: QuizPanel merges the draft through useAIForm's handler, and ApplyContent records it and
  // marks the reply applied, so a second apply is refused. The draft travels as JSON, uncleaned.
  const handlePutQuizDraftInEditor = async (messageId: string, draft: QuizDraftDto, description: QuizDraftDescription): Promise<boolean> => {
    const message = resolveMessage(messageId)
    if (!fieldUpdateHandler.value) return false
    const applied = await applyToField(QUIZ_AI_FIELD, JSON.stringify(draft), undefined, message?.backendIndex, { skipMarkdownCleanup: true })
    if (applied) markQuizDraftApplied(messageId, description)
    return applied
  }

  return (
    <div className="ai-chat-widget">
      {/* Floating Button - hide when chat is open */}
      {!state.isOpen && !aiChatSuppressed.value && (
        <AIChatButton onClick={handleToggleChat} hasNotification={hasNotification} />
      )}

      {/* Chat Window */}
      <AIChatWindow
        state={state}
        onClose={closeChat}
        onMinimize={minimizeChat}
        onExpand={maximizeChat}
        onSendMessage={sendUserMessage}
        onSelectVariation={handleSelectVariation}
        onSelectAllVariations={handleSelectAllVariations}
        onExecuteAction={executeAction}
        onMessageFeedback={provideMessageFeedback}
        onPutQuizDraftInEditor={handlePutQuizDraftInEditor}
      />

      {/* Delayed Feedback Banner (Flow 2) */}
      {state.showDelayedFeedback && (
        <DelayedFeedbackBanner
          onRate={(rating) => {
            provideFeedback(rating)
            hideDelayedFeedback()
          }}
        />
      )}
    </div>
  )
}
